import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Component, EventEmitter, Input, Output, NgModule } from '@angular/core';
import tippy, { createSingleton } from 'tippy.js';

const defaultConfig = {
    bladeCount: 15,
    bladeFill: '#D8D8D8',
    bladeGap: 7,
    bladeHeight: 130,
    bladeLightYOffset: 40,
    bladeWidth: 21,
    bladeXOffset: 18,
    chassisHeight: 145,
    defaultFill: '#D8D8D8',
    defaultStroke: '#C5C5C5',
    driveCount: 0,
    driveFill: '#707070',
    driveStroke: '#FFF',
    efmHeight: 45,
    fmCount: 2,
    fmFill: '#DBDBDB',
    fmHeight: 45,
    fmWidth: 425,
    idEnabled: true,
    idDisabledFill: '#CCC',
    idEnabledFill: '#00F',
    psuCount: 4,
    statusFill: '#CACACA',
    statusStroke: '#F0F0F0',
    statusStrokeWidth: 0.5,
    summary: false,
    whiteFill: '#FDFDFD',
    width: 440
};
const summaryConfig = {
    ...defaultConfig,
    bladeGap: 5,
    bladeHeight: 48,
    bladeWidth: 13,
    bladeXOffset: 22.5,
    chassisHeight: 100,
    fmHeight: 13,
    fmWidth: 265,
    idEnabled: false,
    statusStroke: 'none',
    summary: true,
    width: 300
};
const gen2ChassisConfig = {
    ...defaultConfig,
    bladeCount: 10,
    bladeHeight: 165,
    bladeLightYOffset: 92,
    bladeWidth: 35,
    chassisHeight: 180,
    driveCount: 4,
    driveWidth: 13,
    driveHeight: 60,
    efmHeight: 90,
    fmHeight: 63
};
const gen2ChassisSummaryConfig = {
    ...defaultConfig,
    bladeCount: 10,
    bladeGap: 5,
    bladeHeight: 68,
    bladeWidth: 22,
    bladeXOffset: 22.5,
    chassisHeight: 122,
    driveCount: 4,
    fmHeight: 13,
    fmWidth: 265,
    idEnabled: false,
    statusStroke: 'none',
    summary: true,
    width: 300
};
/**
 * Returns a configuration object which is required to initialize each chassis and XFM.
 * @param isSummary true if rendering a summary view, false if rendering a detailed view.
 * @param chassisGeneration - original chassis are generation 1; //S and //E chassis are generation 2.
 * @param identifyEnabled true if the user can interact with the identify lights; false otherwise.
 */
const getConfig = (isSummary = false, chassisGeneration = 1, identifyEnabled = true) => {
    let cfg = chassisGeneration === 2
        ? (isSummary ? gen2ChassisSummaryConfig : gen2ChassisConfig)
        : (isSummary ? summaryConfig : defaultConfig);
    if (!identifyEnabled) {
        cfg = { ...cfg, idEnabled: identifyEnabled };
    }
    return cfg;
};
// The list of all component status strings.
const VALID_COMPONENT_STATUSES = [
    'critical',
    'disconnected',
    'evacuated',
    'evacuating',
    'healthy',
    'identifying',
    'initializing',
    'unclaimed',
    'unhealthy',
    'unknown',
    'unrecognized',
    'unused',
];
const componentStatusFromJson = (jsonStatus) => VALID_COMPONENT_STATUSES.includes(jsonStatus) ? jsonStatus : 'unknown';
const isNumber = (num) => num != null && typeof (num) === 'number' && !Number.isNaN(num);
const getReadableStatus = (status, progress, storageStatus) => {
    if (status) {
        let readableStatus = status.slice();
        // Remove snake casing and capitalize first character
        readableStatus = readableStatus
            .replace(/_/g, ' ')
            .replace(/^([a-z])/, (char) => char.toUpperCase());
        if (storageStatus && storageStatus !== status) {
            const secondaryStatus = storageStatus
                .replace(/_/g, ' ')
                .replace(/^([a-z])/, (char) => char.toUpperCase());
            readableStatus += ' / ' + secondaryStatus;
        }
        return readableStatus;
    }
    else {
        return '';
    }
};
/**
 * Function to use in Angular templates that use ngFor to loop over lists of components, for example:
 * @example
 * <div *ngFor="let c of chassis; trackBy: trackByFn">
 * @param index ngFor index
 * @param component ngFor item
 */
const trackByFn = (index, component) => component.name;
/**
 * Returns a color string for the provided status.
 * @param status status (see {@link VALID_COMPONENT_STATUSES})
 *
 * @see https://wiki.purestorage.com/display/IR/FlashBlade+Hardware+And+Software+Statuses
 * @see https://wiki.purestorage.com/display/IR/Congo+Hardware+status+details
 */
const getColorForStatus = (status) => {
    if (['healthy'].includes(status)) {
        return '#8dc63f';
    }
    else if (['identifying', 'initializing', 'unhealthy', 'unrecognized'].includes(status)) {
        return '#e8c721';
    }
    else if (['critical', 'unknown'].includes(status)) {
        return '#d84c41';
    }
    else if (['disconnected', 'evacuated', 'evacuating', 'unclaimed'].includes(status)) {
        return '#8f8f8f';
    }
    else if (['unused'].includes(status)) {
        return '#e3e3e3';
    }
    else {
        return '#cacaca';
    }
};
/**
 * Formats a capacity value.
 * @param bytes capacity in bytes
 */
const formatCapacity = (bytes) => format(bytes, 1024, ['B', 'K', 'M', 'G', 'T', 'P']);
/**
 * Formats a network speed value.
 * @param speed network speed in bytes per second
 */
const formatNetworkSpeed = (speed) => format(speed, 1000, ['b/s', 'Kb/s', 'Mb/s', 'Gb/s', 'Tb/s', 'Pb/s']);
// TODO: ideally should refactor pure-ui to extract utility classes like FormatUtils so that this is not duplicated.
const format = (n, div, units) => {
    if (n === null || n === undefined) {
        return '-';
    }
    if (n === 0) {
        return '0.00';
    }
    let index = 0;
    let value = n;
    while (index < units.length - 1 && value / div >= 1) {
        value /= div;
        index++;
    }
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + units[index];
};

/**
 * Represents a FlashBlade hardware component as returned by `GET /hardware`.
 */
class HardwareComponent {
    constructor(hardware, type) {
        this.readableDetails = [];
        this.readableStatus = '';
        this.name = hardware.name;
        this.identify = hardware.identify_enabled;
        this.id = hardware.id;
        this.slot = hardware.slot;
        this.sn = hardware.serial;
        this.pn = hardware.part_number;
        this.model = hardware.model;
        this.status = componentStatusFromJson(hardware.status);
        this.readableStatus = getReadableStatus(this.status);
        this.type = type;
        this.temperature = hardware.temperature != null ? hardware.temperature : undefined;
        if (hardware.details) {
            this.readableDetails.push(hardware.details);
        }
    }
    chassis() {
        return this.name.split('.')[0];
    }
    parent() {
        const parts = this.name.split('.');
        return parts.slice(0, parts.length - 1).join('.');
    }
    onXFM() {
        return this.name.split('.')[0].startsWith('XFM');
    }
    key() {
        const ar = this.name.split('.');
        return ar[0].startsWith('XFM') ? ar[0] : `${ar[0]}.${ar[1]}`;
    }
    // Typically used to complete creation; for example, to add missing children based on configuration
    setConfig(config) {
        this.config = config;
    }
}
/**
 * Common base class for blade and drive hardware components.
 */
class StorageComponent extends HardwareComponent {
    constructor(hw, type, storage) {
        super(hw, type);
        this.capacity = 0;
        if (storage != null) {
            this.capacity = storage.raw_capacity;
            if (storage.progress != null) {
                // middleware takes progress from inuk and divides by 100.0 so here we reverse that
                const modifiedProgress = (storage.progress * 100.0);
                this.progress = Math.floor(modifiedProgress);
            }
            // In general the status shown is from GET /hardware, but the "evacuated" and "evacuating"
            // states are specific to GET /blades and GET /drives.
            this.secondaryStatus = componentStatusFromJson(storage.status);
            this.readableStatus = getReadableStatus(this.status, undefined, this.secondaryStatus);
            if (storage.details && hw.details !== storage.details) {
                this.readableDetails.push(storage.details);
            }
        }
    }
}

class Blade extends StorageComponent {
    constructor(hw, mwBlade) {
        super(hw, 'Blade', mwBlade);
        this.drives = [];
    }
    setConfig(config) {
        super.setConfig(config);
        if (config.driveCount > 0 && this.status !== 'unused') {
            this.drives = this.drives ?? [];
        }
    }
}

class Chassis extends HardwareComponent {
    constructor(hw) {
        super(hw, 'Chassis');
        this.blades = [];
        this.fabricModules = [];
        this.powerSupplies = [];
        this.powerSupplyModules = [];
        this.generation = 1;
        this.index = hw.index;
    }
    /**
     * Initialize the chassis using the provided configuration.
     * @param config configuration (see {@link getConfig}).
     */
    setConfig(config) {
        super.setConfig(config);
        this.blades = this.blades ?? [];
        this.blades.forEach((blade) => blade.setConfig(config));
        this.fabricModules = this.fabricModules ?? [];
        this.fabricModules.forEach((fm) => fm.setConfig(config));
        if (this.generation === 2 && this.powerSupplyModules.length > 0) {
            // PSMs returned on Gen2 real hardware clusters.
            this.powerSupplyModules = this.powerSupplyModules ?? [];
        }
        else {
            // No PSMs on Norway or Gen2 SIM clusters.
            this.powerSupplies = this.powerSupplies ?? [];
        }
    }
}

class Drive extends StorageComponent {
    constructor(hw, mwDrive) {
        super(hw, mwDrive?.type === 'DFMe' ? 'DirectFlash Module (e)' : 'DirectFlash Module', mwDrive);
    }
}

const regex = new RegExp(/([A-Z]+)(\d+)(?:\.([A-Z]+)(\d+))?(?:\.([A-Z]+)(\d+))?/);
const transform = (name) => name.match(regex).slice(1).map((s, i) => s === undefined ? (i % 2 === 0 ? '' : -1) : (i % 2 === 0 ? s : parseInt(s, 10)));
/**
 * Sorts an array of objects which have a `name` key whose values are of the form: `CH1.FB1.BAY1`.
 * This is equivalent to the middleware `(natural)` sort but is needed here to ensure that lists are sorted correctly.
 * @param left left item
 * @param right right item
 */
const sortHw = (left, right) => {
    const l = transform(left.name);
    const r = transform(right.name);
    for (let i = 0; i < l.length; i++) {
        if (l[i] < r[i]) {
            return -1;
        }
        else if (l[i] > r[i]) {
            return 1;
        }
    }
    return 0;
};
/**
 * Converts rpc `purehw.list` output to middleware `GET purehw` format.
 * @param rpc rpc json of single hardware component
 */
const rpcToPureHw = (rpc) => {
    const map = {
        chassis: 'ch', controller: 'fm', eth_port: 'eth', fan: 'fan', flash_blade: 'fb', mgmt_port: 'mgmt_port',
        psm: 'psm', power_supply: 'pwr', am: 'xfm', drive: 'bay'
    };
    return {
        details: rpc.details,
        id: rpc.sn,
        identify_enabled: rpc.identify?.toLowerCase() === 'on',
        index: rpc.index,
        model: rpc.model,
        name: rpc.name,
        part_number: rpc.part_number,
        serial: rpc.sn,
        slot: rpc.slot != null ? parseInt(rpc.slot, 10) : undefined,
        speed: rpc.max_speed != null ? parseInt(rpc.max_speed, 10) : undefined,
        status: rpc.status,
        temperature: rpc.temperature,
        type: map?.[rpc.type] ?? rpc.type
    };
};
/**
 * Converts rpc `pureblade.list` output to middleware `GET pureblade` format.
 * @param rpc rpc json of single blade
 */
const rpcToPureBlade = (rpc) => {
    return {
        ...rpcToPureHw(rpc),
        progress: isNumber(rpc.progress) ? (rpc.progress / 100.0) : undefined,
        raw_capacity: rpc.capacity,
        target: null
    };
};
/**
 * Converts rpc `puredrive.list` output to middleware `GET puredrive` format.
 * @param rpc rpc json of single drive
 */
const rpcToPureDrive = (rpc) => {
    return {
        ...rpcToPureBlade(rpc),
        slot: rpc.drive_bay,
        type: rpc.drive_type
    };
};

// Computes the number of ports per slot.
const computePortCounts = (parent, ethPorts) => {
    // Compute portCount for each port. This should be one of:
    //   1 - grouped port
    //   2 - XFMv2 ungrouped ports
    //   4 - ungrouped ports
    const ports = ethPorts.sort(sortHw);
    const slotCounts = new Map;
    ports.forEach((p) => {
        if (slotCounts.has(p.slot)) {
            slotCounts.set(p.slot, 1);
        }
        else {
            const c = slotCounts.get(p.slot);
            slotCounts.set(p.slot, c + 1);
        }
    });
    ports.forEach((p) => p.portCount = slotCounts.get(p.slot));
    return ports;
};
class EthPort extends HardwareComponent {
    constructor(hw) {
        super(hw, 'Ethernet Port');
        this.index = hw.index;
        this.speed = hw.speed;
        // isGrouped is calculated from index
    }
    get isGrouped() {
        return this.index === null;
    }
}

class ExternalFabricModule extends HardwareComponent {
    constructor(hw) {
        super(hw, 'eXternal Fabric Module');
        this.PSU_COUNT = 2;
        this.ethPorts = [];
        this.powerSupplies = [];
        this.fans = [];
        this.version = 1;
        // XFMv1 has 4 fans; XFMv2 has 7 fans.
        this.fanCount = () => this.version === 2 ? 7 : 4;
        if (!this.index) {
            // purehw currently does not provide a value for 'index'; use the value of 'slot' instead
            this.index = this.slot;
        }
        // TODO: should we hard-code the model number or use the fact that there are 14 fans?
        if (this.model === 'XFM-8400') {
            this.version = 2;
        }
    }
    /**
     * Initialize the XFM using the provided configuration.
     * @param config configuration (see {@link getConfig}).
     */
    setConfig(config) {
        super.setConfig(config);
        this.ethPorts = computePortCounts(`XFM${this.slot}`, this.ethPorts ?? []);
        if (this.model) {
            this.fans = this.fans ?? [];
            this.powerSupplies = this.powerSupplies ?? [];
            if (this.version === 2) {
                this.powerSupplies.reverse();
                this.fans = this.fans.slice(0, this.fanCount()).reverse();
            }
        }
        else {
            // fans and power supplies depend on the model string
            this.fans = [];
            this.powerSupplies = [];
        }
    }
}

class FabricModule extends HardwareComponent {
    constructor(hw) {
        super(hw, 'Fabric Module');
        this.ethPorts = [];
        // fm fans aren't shown in GUI, but xfm fans are. Leaving in the object model for now.
        this.fans = [];
    }
    setConfig(config) {
        super.setConfig(config);
        this.ethPorts = computePortCounts(`FM${this.slot}`, this.ethPorts ?? []);
    }
}

class Fan extends HardwareComponent {
    constructor(hw) {
        super(hw, 'Fan');
    }
}

class MgmtPort extends HardwareComponent {
    constructor(hw) {
        super(hw, 'Management Port');
        this.index = hw.index;
        this.speed = hw.speed;
    }
}

class PowerSupply extends HardwareComponent {
    constructor(hw) {
        super(hw, 'Power Supply');
    }
}

class PowerSupplyModule extends HardwareComponent {
    constructor(hw) {
        super(hw, 'Power Supply Module');
    }
}

/**
 * Creates an {@link ArrayHardware} object that is used to render svg visualizations of hardware health based on
 * middleware responses.
 * @param hardwareList list of hardware components returned by `GET /hardware`
 * @param bladeList list of blades returned by `GET /blades`
 * @param driveList list of drives returned by `GET /drives`
 */
const buildBoxes = (hardwareList, bladeList, driveList) => {
    const hw = { chassis: [], efms: [], rawCapacity: 0 };
    const bladeMap = new Map();
    const driveMap = new Map();
    const chassisMap = new Map();
    const xfmMap = new Map();
    const fmMap = new Map();
    hardwareList.sort(sortHw);
    bladeList.sort(sortHw);
    driveList.sort(sortHw);
    bladeList.forEach((b) => bladeMap.set(b.name, b));
    driveList.forEach((d) => driveMap.set(d.name, d));
    // Since the hardware components are sorted by name using `(natural)` sorting, a single pass is sufficient
    // (parents are guaranteed to be processed before children).
    for (const hardware of hardwareList) {
        switch (hardware.type) {
            case 'ch': {
                const chassis = new Chassis(hardware);
                chassis.generation = ['CH-FB//S', 'CH-FB-II'].includes(chassis.model) ? 2 : 1;
                hw.chassis.push(chassis);
                chassisMap.set(chassis.name, chassis);
                break;
            }
            case 'xfm': {
                const efm = new ExternalFabricModule(hardware);
                hw.efms.push(efm);
                xfmMap.set(efm.name, efm);
                break;
            }
            case 'fm': {
                const fabricModule = new FabricModule(hardware);
                // fm names are "CH1.FM1"
                chassisMap.get(fabricModule.chassis()).fabricModules.push(fabricModule);
                fmMap.set(fabricModule.name, fabricModule);
                break;
            }
            case 'fb':
                if (bladeMap.has(hardware.name)) {
                    const blade = new Blade(hardware, bladeMap.get(hardware.name));
                    chassisMap.get(blade.chassis()).blades.push(blade);
                }
                break;
            case 'eth': {
                const ethPort = new EthPort(hardware);
                (ethPort.onXFM() ? xfmMap : fmMap).get(ethPort.key()).ethPorts.push(ethPort);
                break;
            }
            case 'mgmt_port': {
                const mgmtPort = new MgmtPort(hardware);
                (mgmtPort.onXFM() ? xfmMap : fmMap).get(mgmtPort.key()).mgmtPort = mgmtPort;
                break;
            }
            case 'pwr': {
                const powerSupply = new PowerSupply(hardware);
                const chassis = powerSupply.chassis();
                (chassisMap.has(chassis) ? chassisMap : xfmMap).get(chassis).powerSupplies.push(powerSupply);
                break;
            }
            case 'psm': {
                const powerSupplyModule = new PowerSupplyModule(hardware);
                const chassis = powerSupplyModule.chassis();
                chassisMap.get(chassis).powerSupplyModules.push(powerSupplyModule);
                break;
            }
            case 'fan': {
                const fan = new Fan(hardware);
                (fan.onXFM() ? xfmMap : fmMap).get(fan.key()).fans.push(fan);
                break;
            }
            case 'bay':
                if (driveMap.has(hardware.name)) {
                    const drive = new Drive(hardware, driveMap.get(hardware.name));
                    const chassis = chassisMap.get(drive.chassis());
                    const parent = drive.parent();
                    const blade = chassis.blades.find((b) => b.name === parent);
                    // `GET /hardware` should not be returning any drives for 'unused' blades, but it currently does
                    // (see: IR-107072, IR-108926). This 'if' is a workaround to ignore such drives.
                    if (blade && blade.status !== 'unused') {
                        blade.drives.push(drive);
                    }
                }
                break;
            default:
                console.warn(`Unknown hardware type: ${hardware.type}`);
        }
    }
    for (const chassis of hw.chassis) {
        if (chassis.generation === 2 && chassis.powerSupplyModules.length > 0) {
            // Nest power supply units under their associated power supply modules.
            for (const psm of chassis.powerSupplyModules) {
                const j = chassis.powerSupplies.findIndex((psu) => psu.slot === psm.slot);
                if (j > -1) {
                    psm.powerSupply = chassis.powerSupplies[j];
                }
            }
        }
    }
    return hw;
};

class GraphicContainerComponent {
    constructor() {
        this.count = GraphicContainerComponent.count++;
        this.tippyInstances = [];
        this.singleton = createSingleton([], {
            allowHTML: true,
            arrow: true,
            delay: [400, 200],
            interactive: true,
            appendTo: document.body
        });
        this.trackByFn = (index, component) => trackByFn(index, component);
        // returns Math.floor
        this.fl = (n) => Math.floor(n);
        // Converts 1-based slot to 0-based index.
        this.s2i = (i) => i == null ? 0 : i - 1;
    }
    ngOnDestroy() {
        this.destroyTooltip();
    }
    applyTooltip(map) {
        const tooltipMap = new Map;
        this.singleton.hide();
        const getContent = (e) => {
            const name = e.getAttribute('data-name');
            if (name) {
                const c = tooltipMap.get(name);
                return c ? this.getTooltipContent(c) : '';
            }
        };
        const instances = [];
        for (const selector of Object.keys(map)) {
            const comps = map[selector];
            comps.forEach((c) => tooltipMap.set(c.name, c));
        }
        for (const selector of Object.keys(map)) {
            instances.push(...tippy(`[data-instance="${this.count}"].fb-gui-hw ${selector}`, { content: (e) => getContent(e) }));
        }
        this.tippyInstances.forEach((instance) => instance.destroy());
        this.tippyInstances = instances;
        this.singleton.setInstances(instances);
    }
    destroyTooltip() {
        this.tippyInstances.forEach((instance) => instance.destroy());
        this.singleton.destroy();
        this.tippyInstances = [];
    }
    getTooltipContent(component) {
        // TODO: fix
        let capacity;
        let speed;
        const c = component;
        if (c.capacity !== undefined) {
            capacity = formatCapacity(c.capacity);
        }
        if (c.speed !== undefined) {
            speed = formatNetworkSpeed(c.speed);
        }
        return `
            <table class="fb-gui-hw-tooltip">
                <tr>
                    <td class="tip-title" colspan="2">${component.type} | ${component.name}</td>
                </tr>
                ${component.readableStatus ?
            `<tr>
                    <td>Status:</td>
                    <td>${component.readableStatus}</td>
                </tr>` : ``}
                ${component.readableDetails.length > 0 ?
            `<tr>
                    <td>Details:</td>
                    <td>${component.readableDetails.join('<br>')}</td>
                </tr>` : ``}
                ${component.slot ?
            `<tr>
                    <td>Slot:</td>
                    <td>${component.slot || '-'}</td>
                </tr>` : ``}
                ${capacity !== undefined ?
            `<tr>
                    <td>Raw Capacity:</td>
                    <td>${capacity}</td>
                </tr>` : ``}
                ${speed !== undefined ?
            `<tr>
                    <td>Speed:</td>
                    <td>${speed}</td>
                </tr>` : ``}
                ${component.temperature !== undefined ?
            `<tr>
                    <td>Temperature:</td>
                    <td>${component.temperature} &deg;C</td>
                </tr>` : ``}
                ${component.model ?
            `<tr>
                    <td>Model:</td>
                    <td>${component.model}</td>
                </tr>` : ``}
                ${component.sn ?
            `<tr>
                    <td>Serial:</td>
                    <td>${component.sn}</td>
                </tr>` : ``}
                ${component.pn ?
            `<tr>
                    <td>Part Number:</td>
                    <td>${component.pn}</td>
                </tr>` : ``}
            </table>
        `;
    }
}
GraphicContainerComponent.count = 0;
/** @nocollapse */ GraphicContainerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: GraphicContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ GraphicContainerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.9", type: GraphicContainerComponent, selector: "ng-component", ngImport: i0, template: ``, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: GraphicContainerComponent, decorators: [{
            type: Component,
            args: [{ template: `` }]
        }] });

class IdentifyLightComponent {
    constructor() {
        this.cx = 10.5;
        this.cy = 10;
        this.onStatusRect = false;
        this.toggle = new EventEmitter();
    }
    doToggle() {
        this.toggle.emit({ name: this.component.name, enabled: !this.component.identify });
    }
}
/** @nocollapse */ IdentifyLightComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: IdentifyLightComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ IdentifyLightComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.9", type: IdentifyLightComponent, selector: "[identifyLight]", inputs: { component: "component", config: "config", cx: "cx", cy: "cy", onStatusRect: "onStatusRect" }, outputs: { toggle: "toggle" }, ngImport: i0, template: `
        <svg:circle [ngClass]="component.identify ? 'on' : 'off'"
                    stroke="white" stroke-width="1"
                    [attr.fill]="component.identify ? '#00F' : '#E3E3E3'"
                    [attr.cx]="cx" [attr.cy]="cy" r="4"
                    [attr.cursor]="config.idEnabled ? 'pointer' : 'default'"
                    [attr.fill-opacity]="component.identify || onStatusRect ? 1 : 0"
                    (click)="config.idEnabled && doToggle()">
            <title>{{ config.idEnabled ? 'Toggle Identify Light' : 'Identify Light' }}</title>
        </svg:circle>
    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: IdentifyLightComponent, decorators: [{
            type: Component,
            args: [{
                    // eslint-disable-next-line
                    selector: '[identifyLight]',
                    template: `
        <svg:circle [ngClass]="component.identify ? 'on' : 'off'"
                    stroke="white" stroke-width="1"
                    [attr.fill]="component.identify ? '#00F' : '#E3E3E3'"
                    [attr.cx]="cx" [attr.cy]="cy" r="4"
                    [attr.cursor]="config.idEnabled ? 'pointer' : 'default'"
                    [attr.fill-opacity]="component.identify || onStatusRect ? 1 : 0"
                    (click)="config.idEnabled && doToggle()">
            <title>{{ config.idEnabled ? 'Toggle Identify Light' : 'Identify Light' }}</title>
        </svg:circle>
    `
                }]
        }], propDecorators: { component: [{
                type: Input
            }], config: [{
                type: Input
            }], cx: [{
                type: Input
            }], cy: [{
                type: Input
            }], onStatusRect: [{
                type: Input
            }], toggle: [{
                type: Output
            }] } });

class StatusComponent {
    constructor() {
        this.x = 0;
        this.y = 0;
        this.width = 10;
        this.height = 10;
        this.summary = false;
        this.getColorForStatus = getColorForStatus;
    }
    // For now only show indicators for blades and drives.
    shouldDisplayIndicator(c) {
        return !this.summary &&
            (this.isStorage() /*|| this.isOuter()*/) &&
            c.readableDetails.length > 0 &&
            c.status !== 'unused';
    }
    isStorage() {
        return ['Blade', 'DirectFlash Module', 'DirectFlash Module (e)'].includes(this.component.type);
    }
    isOuter() {
        return ['Chassis', 'Fabric Module', 'eXternal Fabric Module', 'Power Supply Module'].includes(this.component.type);
    }
    getCx() {
        return this.isOuter() ? this.x + this.width / 2 : this.x + this.width;
    }
    getCy() {
        return this.isOuter() ? this.y + 5 : this.y;
    }
}
/** @nocollapse */ StatusComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: StatusComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ StatusComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.9", type: StatusComponent, selector: "[hwStatus]", inputs: { component: "component", x: "x", y: "y", width: "width", height: "height", klass: "klass", stroke: "stroke", strokeWidth: "strokeWidth", summary: "summary" }, ngImport: i0, template: `
        <svg:rect [ngClass]="klass" class="hw-status"
                  [attr.data-name]="component.name ?? null"
                  [attr.x]="x"
                  [attr.y]="y"
                  [attr.width]="width"
                  [attr.height]="height"
                  [attr.stroke]="stroke !== undefined ? stroke : null"
                  [attr.stroke-width]="strokeWidth !== undefined ? strokeWidth : null"
                  [attr.fill]="getColorForStatus(component.status)">
        </svg:rect>
        <svg:circle *ngIf="shouldDisplayIndicator(component)" r="3" [attr.cx]="getCx()" [attr.cy]="getCy()"
                    fill="#333" stroke="white" stroke-width="0.5">
        </svg:circle>
    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: StatusComponent, decorators: [{
            type: Component,
            args: [{
                    // eslint-disable-next-line
                    selector: '[hwStatus]',
                    template: `
        <svg:rect [ngClass]="klass" class="hw-status"
                  [attr.data-name]="component.name ?? null"
                  [attr.x]="x"
                  [attr.y]="y"
                  [attr.width]="width"
                  [attr.height]="height"
                  [attr.stroke]="stroke !== undefined ? stroke : null"
                  [attr.stroke-width]="strokeWidth !== undefined ? strokeWidth : null"
                  [attr.fill]="getColorForStatus(component.status)">
        </svg:rect>
        <svg:circle *ngIf="shouldDisplayIndicator(component)" r="3" [attr.cx]="getCx()" [attr.cy]="getCy()"
                    fill="#333" stroke="white" stroke-width="0.5">
        </svg:circle>
    `
                }]
        }], propDecorators: { component: [{
                type: Input
            }], x: [{
                type: Input
            }], y: [{
                type: Input
            }], width: [{
                type: Input
            }], height: [{
                type: Input
            }], klass: [{
                type: Input
            }], stroke: [{
                type: Input
            }], strokeWidth: [{
                type: Input
            }], summary: [{
                type: Input
            }] } });

class ChassisBackComponent extends GraphicContainerComponent {
    constructor() {
        super(...arguments);
        this.toggle = new EventEmitter();
    }
    ngOnChanges() {
        if (!this.chassis) {
            return;
        }
        this.cfg = this.chassis.config;
        if (!this.cfg.summary) {
            setTimeout(() => {
                const ports = [];
                for (const fm of this.chassis.fabricModules) {
                    for (const port of fm.ethPorts) {
                        ports.push(port);
                    }
                    if (fm.mgmtPort) {
                        ports.push(fm.mgmtPort);
                    }
                }
                this.applyTooltip({
                    'rect.fm.hw-status': this.chassis.fabricModules,
                    'rect.psm.hw-status': this.chassis.powerSupplyModules,
                    'rect.psu.hw-status': this.chassis.powerSupplies,
                    'rect.eth.hw-status': ports
                });
            }, 1);
        }
    }
}
/** @nocollapse */ ChassisBackComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: ChassisBackComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ ChassisBackComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.9", type: ChassisBackComponent, selector: "fb-hw-chassis-back", inputs: { chassis: "chassis" }, outputs: { toggle: "toggle" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: `
        <svg class="fb-gui-hw" [attr.data-instance]="count" [attr.viewBox]="'0 0 ' + (cfg.width) + ' ' + (cfg.chassisHeight)">
            <g>
                <rect x="0" y="0" [attr.width]="cfg.width" [attr.height]="cfg.chassisHeight"
                      fill="white" [attr.stroke]="cfg.defaultStroke">
                </rect>
            </g>
            <g *ngFor="let fm of chassis.fabricModules; let j=index; trackBy: trackByFn"
               [attr.transform]="'translate(8, ' + (j * (cfg.fmHeight + 10) + 8) + ')'">
                <rect x="0"
                      y="0"
                      [attr.width]="cfg.fmWidth"
                      [attr.height]="cfg.fmHeight"
                      [attr.fill]="cfg.fmFill">
                </rect>
                <g hwStatus
                   [klass]="'fm'"
                   [component]="fm"
                   [strokeWidth]="0.5"
                   [height]="cfg.fmHeight">
                </g>
                <g *ngIf="!cfg.summary" identifyLight [component]="fm" [config]="cfg"
                   [onStatusRect]="true" (toggle)="toggle.emit($event)"
                   [cx]="5" [cy]="cfg.fmHeight / 2"></g>
                <ng-container *ngIf="chassis.generation === 2">
                    <g *ngFor="let port of fm.ethPorts; let k=index; trackBy: trackByFn"
                       [attr.transform]="'translate(50, ' + (cfg.fmHeight - 25) + ')'">
                        <g hwStatus
                           [klass]="'eth'"
                           [component]="port"
                           [x]="(port.slot > 4 ? 1 : 0) * ((cfg.fmWidth - 60) / 2 + 30) + (s2i(port.slot) % 4) * 30
                               + (fl(s2i(port.index) / 2) * 9.5)"
                           [y]="(s2i(port.index) % 2) * 8.5"
                           [width]="port.isGrouped ? 18 : 8.5"
                           [height]="port.isGrouped ? 16 : 7.5">
                        </g>
                    </g>
                    <g *ngIf="fm.mgmtPort"
                       [attr.transform]="'translate(' + (cfg.fmWidth / 2 - 10) + ', ' + (cfg.fmHeight - 25) + ')'">
                        <g hwStatus
                           [klass]="'eth'"
                           [component]="fm.mgmtPort"
                           [width]="8.5"
                           [height]="7.5">
                        </g>
                    </g>
                </ng-container>
                <ng-container *ngIf="chassis.generation === 1">
                    <g *ngFor="let port of fm.ethPorts; let k=index; trackBy: trackByFn"
                       [attr.transform]="'translate(' + (cfg.fmWidth / 2 - 18) + ', 5.5)'">
                        <g hwStatus
                           [klass]="'eth'"
                           [component]="port"
                           [x]="fl(s2i(port.slot) / 2) * 20 + (fl(s2i(port.index) / 2) * 9.5)"
                           [y]="(s2i(port.slot) % 2) * 18 + (s2i(port.index) % 2) * 8.5"
                           [width]="port.isGrouped ? 18 : 8.5"
                           [height]="port.isGrouped ? 16 : 7.5">
                        </g>
                    </g>
                    <g *ngIf="fm.mgmtPort"
                       [attr.transform]="'translate(' + (cfg.fmWidth / 2 - 31) + ', 5.5)'">
                        <g hwStatus
                           [klass]="'eth'"
                           [component]="fm.mgmtPort"
                           [width]="8.5"
                           [height]="7.5">
                        </g>
                    </g>
                </ng-container>
            </g>
            <ng-container *ngIf="chassis.powerSupplyModules.length === 0">
                <g *ngFor="let psu of chassis.powerSupplies; let j=index; trackBy: trackByFn"
                   [attr.transform]="'translate(' + (8 + j * 109) + ', ' + (8 + 2 * (cfg.fmHeight + 10)) + ')'">
                    <rect x="0" y="0" width="98" height="20" [attr.fill]="cfg.fmFill"></rect>
                    <g hwStatus
                       [klass]="'psu'"
                       [component]="psu"
                       [x]="5"
                       [y]="5"
                       [strokeWidth]="cfg.statusStrokeWidth"
                       [stroke]="cfg.statusStroke">
                    </g>
                </g>
            </ng-container>
            <ng-container *ngIf="chassis.powerSupplyModules.length > 0">
                <g *ngFor="let psm of chassis.powerSupplyModules; let j=index; trackBy: trackByFn"
                   [attr.transform]="'translate(' + (8 + j * 109) + ', ' + (8 + 2 * (cfg.fmHeight + 10)) + ')'">
                    <rect x="0" y="0" width="98" height="20" [attr.fill]="cfg.fmFill"></rect>
                    <g hwStatus
                       [klass]="'psm'"
                       [component]="psm"
                       [strokeWidth]="0.5"
                       [height]="20">
                    </g>
                    <g *ngIf="psm.powerSupply" hwStatus
                       [klass]="'psu'"
                       [component]="psm.powerSupply"
                       [x]="48.5"
                       [y]="5"
                       [strokeWidth]="cfg.statusStrokeWidth"
                       [stroke]="cfg.statusStroke">
                    </g>
                </g>
            </ng-container>
        </svg>
    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IdentifyLightComponent, selector: "[identifyLight]", inputs: ["component", "config", "cx", "cy", "onStatusRect"], outputs: ["toggle"] }, { kind: "component", type: StatusComponent, selector: "[hwStatus]", inputs: ["component", "x", "y", "width", "height", "klass", "stroke", "strokeWidth", "summary"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: ChassisBackComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fb-hw-chassis-back',
                    template: `
        <svg class="fb-gui-hw" [attr.data-instance]="count" [attr.viewBox]="'0 0 ' + (cfg.width) + ' ' + (cfg.chassisHeight)">
            <g>
                <rect x="0" y="0" [attr.width]="cfg.width" [attr.height]="cfg.chassisHeight"
                      fill="white" [attr.stroke]="cfg.defaultStroke">
                </rect>
            </g>
            <g *ngFor="let fm of chassis.fabricModules; let j=index; trackBy: trackByFn"
               [attr.transform]="'translate(8, ' + (j * (cfg.fmHeight + 10) + 8) + ')'">
                <rect x="0"
                      y="0"
                      [attr.width]="cfg.fmWidth"
                      [attr.height]="cfg.fmHeight"
                      [attr.fill]="cfg.fmFill">
                </rect>
                <g hwStatus
                   [klass]="'fm'"
                   [component]="fm"
                   [strokeWidth]="0.5"
                   [height]="cfg.fmHeight">
                </g>
                <g *ngIf="!cfg.summary" identifyLight [component]="fm" [config]="cfg"
                   [onStatusRect]="true" (toggle)="toggle.emit($event)"
                   [cx]="5" [cy]="cfg.fmHeight / 2"></g>
                <ng-container *ngIf="chassis.generation === 2">
                    <g *ngFor="let port of fm.ethPorts; let k=index; trackBy: trackByFn"
                       [attr.transform]="'translate(50, ' + (cfg.fmHeight - 25) + ')'">
                        <g hwStatus
                           [klass]="'eth'"
                           [component]="port"
                           [x]="(port.slot > 4 ? 1 : 0) * ((cfg.fmWidth - 60) / 2 + 30) + (s2i(port.slot) % 4) * 30
                               + (fl(s2i(port.index) / 2) * 9.5)"
                           [y]="(s2i(port.index) % 2) * 8.5"
                           [width]="port.isGrouped ? 18 : 8.5"
                           [height]="port.isGrouped ? 16 : 7.5">
                        </g>
                    </g>
                    <g *ngIf="fm.mgmtPort"
                       [attr.transform]="'translate(' + (cfg.fmWidth / 2 - 10) + ', ' + (cfg.fmHeight - 25) + ')'">
                        <g hwStatus
                           [klass]="'eth'"
                           [component]="fm.mgmtPort"
                           [width]="8.5"
                           [height]="7.5">
                        </g>
                    </g>
                </ng-container>
                <ng-container *ngIf="chassis.generation === 1">
                    <g *ngFor="let port of fm.ethPorts; let k=index; trackBy: trackByFn"
                       [attr.transform]="'translate(' + (cfg.fmWidth / 2 - 18) + ', 5.5)'">
                        <g hwStatus
                           [klass]="'eth'"
                           [component]="port"
                           [x]="fl(s2i(port.slot) / 2) * 20 + (fl(s2i(port.index) / 2) * 9.5)"
                           [y]="(s2i(port.slot) % 2) * 18 + (s2i(port.index) % 2) * 8.5"
                           [width]="port.isGrouped ? 18 : 8.5"
                           [height]="port.isGrouped ? 16 : 7.5">
                        </g>
                    </g>
                    <g *ngIf="fm.mgmtPort"
                       [attr.transform]="'translate(' + (cfg.fmWidth / 2 - 31) + ', 5.5)'">
                        <g hwStatus
                           [klass]="'eth'"
                           [component]="fm.mgmtPort"
                           [width]="8.5"
                           [height]="7.5">
                        </g>
                    </g>
                </ng-container>
            </g>
            <ng-container *ngIf="chassis.powerSupplyModules.length === 0">
                <g *ngFor="let psu of chassis.powerSupplies; let j=index; trackBy: trackByFn"
                   [attr.transform]="'translate(' + (8 + j * 109) + ', ' + (8 + 2 * (cfg.fmHeight + 10)) + ')'">
                    <rect x="0" y="0" width="98" height="20" [attr.fill]="cfg.fmFill"></rect>
                    <g hwStatus
                       [klass]="'psu'"
                       [component]="psu"
                       [x]="5"
                       [y]="5"
                       [strokeWidth]="cfg.statusStrokeWidth"
                       [stroke]="cfg.statusStroke">
                    </g>
                </g>
            </ng-container>
            <ng-container *ngIf="chassis.powerSupplyModules.length > 0">
                <g *ngFor="let psm of chassis.powerSupplyModules; let j=index; trackBy: trackByFn"
                   [attr.transform]="'translate(' + (8 + j * 109) + ', ' + (8 + 2 * (cfg.fmHeight + 10)) + ')'">
                    <rect x="0" y="0" width="98" height="20" [attr.fill]="cfg.fmFill"></rect>
                    <g hwStatus
                       [klass]="'psm'"
                       [component]="psm"
                       [strokeWidth]="0.5"
                       [height]="20">
                    </g>
                    <g *ngIf="psm.powerSupply" hwStatus
                       [klass]="'psu'"
                       [component]="psm.powerSupply"
                       [x]="48.5"
                       [y]="5"
                       [strokeWidth]="cfg.statusStrokeWidth"
                       [stroke]="cfg.statusStroke">
                    </g>
                </g>
            </ng-container>
        </svg>
    `
                }]
        }], propDecorators: { chassis: [{
                type: Input
            }], toggle: [{
                type: Output
            }] } });

class ChassisFrontComponent extends GraphicContainerComponent {
    constructor() {
        super(...arguments);
        this.toggle = new EventEmitter();
    }
    ngOnChanges() {
        if (!this.chassis) {
            return;
        }
        this.cfg = this.chassis.config;
        if (!this.cfg.summary) {
            setTimeout(() => {
                const drives = [];
                for (const blade of this.chassis.blades) {
                    drives.push(...blade.drives.filter((d) => d.status !== 'unused'));
                }
                this.applyTooltip({
                    'rect.chassis.hw-status': [this.chassis],
                    'rect.blade.hw-status': this.chassis.blades,
                    'rect.drive.hw-status': drives
                });
            }, 1);
        }
    }
}
/** @nocollapse */ ChassisFrontComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: ChassisFrontComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ ChassisFrontComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.9", type: ChassisFrontComponent, selector: "fb-hw-chassis-front", inputs: { chassis: "chassis" }, outputs: { toggle: "toggle" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: `
        <svg class="fb-gui-hw" [attr.data-instance]="count" [attr.viewBox]="'0 0 ' + (cfg.width) + ' ' + (cfg.chassisHeight)">
            <g>
                <rect x="0" y="0" [attr.width]="cfg.width" [attr.height]="cfg.chassisHeight"
                      fill="white" [attr.stroke]="cfg.defaultStroke">
                </rect>
                <g hwStatus
                   [klass]="'chassis'"
                   [component]="chassis"
                   [height]="cfg.chassisHeight">
                </g>
                <ng-container *ngIf="chassis.generation === 2 && !cfg.summary">
                    <g identifyLight [component]="chassis" [config]="cfg" [onStatusRect]="true"
                       (toggle)="toggle.emit($event)"
                       [cx]="5" [cy]="cfg.chassisHeight / 2"></g>
                </ng-container>
            </g>
            <g *ngFor="let blade of chassis.blades; let j=index; trackBy: trackByFn"
               [attr.transform]="'translate(' + (cfg.bladeXOffset + j * (cfg.bladeWidth + cfg.bladeGap)) + ', 8)'">
                <rect x="0"
                      y="0"
                      [attr.width]="cfg.bladeWidth"
                      [attr.height]="cfg.bladeHeight"
                      [attr.fill]="blade.status === 'unused' ? 'none' : cfg.defaultFill"
                      [attr.stroke]="blade.status === 'unused' ? cfg.defaultFill : 'none'">
                </rect>
                <g hwStatus
                   [klass]="'blade'"
                   [component]="blade"
                   [x]="cfg.summary ? 2 : 3"
                   [y]="cfg.summary ? 2 : (chassis.generation === 2 ? 4 : 3)"
                   [width]="cfg.bladeWidth - (cfg.summary ? 4 : 6)"
                   [height]="chassis.generation === 2 ? (cfg.summary ? 12 : 14) : cfg.bladeWidth - (cfg.summary ? 4 : 6)"
                   [stroke]="blade.status === 'unused' ? 'none' : '#F0F0F0'"
                   [strokeWidth]="0.5"
                   [summary]="cfg.summary">
                </g>
                <ng-container *ngIf="blade.status !== 'unused' && !cfg.summary">
                    <ng-container *ngIf="chassis.generation === 1">
                        <g identifyLight [component]="blade" [config]="cfg" (toggle)="toggle.emit($event)"
                           [cx]="cfg.bladeWidth / 2"
                           [cy]="cfg.bladeLightYOffset"></g>
                        <line [attr.x1]="cfg.bladeWidth / 2"
                              [attr.y1]="cfg.bladeLightYOffset + 15"
                              [attr.x2]="cfg.bladeWidth / 2"
                              [attr.y2]="cfg.bladeHeight - 10"
                              stroke-width="0.5" stroke="#E8E8E8"></line>
                    </ng-container>
                    <ng-container *ngIf="chassis.generation === 2">
                        <g identifyLight [component]="blade" [config]="cfg" (toggle)="toggle.emit($event)"
                           [cx]="cfg.bladeWidth / 2"
                           [cy]="cfg.bladeLightYOffset"></g>
                        <line x1="4"
                              [attr.y1]="cfg.bladeLightYOffset"
                              [attr.x2]="cfg.bladeWidth / 2 - 7"
                              [attr.y2]="cfg.bladeLightYOffset"
                              stroke="#AAA"></line>
                        <line x1="4"
                              [attr.y1]="cfg.bladeLightYOffset + 0.5"
                              [attr.x2]="cfg.bladeWidth / 2 - 7"
                              [attr.y2]="cfg.bladeLightYOffset + 0.5"
                              stroke="#E8E8E8"></line>
                        <line [attr.x1]="cfg.bladeWidth / 2 + 7"
                              [attr.y1]="cfg.bladeLightYOffset"
                              [attr.x2]="cfg.bladeWidth - 4"
                              [attr.y2]="cfg.bladeLightYOffset"
                              stroke="#AAA"></line>
                        <line [attr.x1]="cfg.bladeWidth / 2 + 7"
                              [attr.y1]="cfg.bladeLightYOffset + 0.5"
                              [attr.x2]="cfg.bladeWidth - 4"
                              [attr.y2]="cfg.bladeLightYOffset + 0.5"
                              stroke="#E8E8E8"></line>
                        <g *ngFor="let drive of blade.drives; let k=index; trackBy: trackByFn"
                           [attr.transform]="'translate(' + (k % 2 === 0 ? 3 : 19) + ',' + (k < 2 ? 23 : 101) + ')'">
                            <rect x="0" y="0" rx="2" stroke-width="0.5"
                                  [attr.width]="cfg.driveWidth"
                                  [attr.height]="cfg.driveHeight"
                                  [attr.stroke]="cfg.driveStroke"
                                  [attr.fill]="drive.status !== 'unused' ? cfg.driveFill : 'none'"></rect>
                            <ng-container *ngIf="drive.status !== 'unused'">
                                <g class="info-tooltip">
                                    <g hwStatus
                                       [klass]="'drive'"
                                       [component]="drive"
                                       [x]="1"
                                       [y]="1"
                                       [width]="cfg.driveWidth-2"
                                       [height]="cfg.driveWidth-2"
                                       [strokeWidth]="0"
                                       [summary]="cfg.summary">
                                    </g>
                                </g>
                                <g identifyLight [component]="drive" [config]="cfg" (toggle)="toggle.emit($event)"
                                   [cx]="6.75" [cy]="51"></g>
                            </ng-container>
                        </g>
                    </ng-container>
                </ng-container>
            </g>
            <ng-container *ngIf="cfg.summary">
                <g *ngFor="let fm of chassis.fabricModules; let j=index; trackBy: trackByFn"
                   [attr.transform]="'translate(' + cfg.bladeXOffset + ', ' + (cfg.bladeHeight + 15 + j * (cfg.fmHeight + 5)) + ')'">
                    <rect x="0" y="0"
                          [attr.width]="cfg.fmWidth"
                          [attr.height]="cfg.fmHeight"
                          [attr.fill]="cfg.fmFill">
                    </rect>
                    <g hwStatus
                       [klass]="'fm'"
                       [component]="fm"
                       [attr.width]="cfg.fmHeight"
                       [attr.height]="cfg.fmHeight">
                    </g>
                </g>
            </ng-container>
        </svg>
    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IdentifyLightComponent, selector: "[identifyLight]", inputs: ["component", "config", "cx", "cy", "onStatusRect"], outputs: ["toggle"] }, { kind: "component", type: StatusComponent, selector: "[hwStatus]", inputs: ["component", "x", "y", "width", "height", "klass", "stroke", "strokeWidth", "summary"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: ChassisFrontComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fb-hw-chassis-front',
                    template: `
        <svg class="fb-gui-hw" [attr.data-instance]="count" [attr.viewBox]="'0 0 ' + (cfg.width) + ' ' + (cfg.chassisHeight)">
            <g>
                <rect x="0" y="0" [attr.width]="cfg.width" [attr.height]="cfg.chassisHeight"
                      fill="white" [attr.stroke]="cfg.defaultStroke">
                </rect>
                <g hwStatus
                   [klass]="'chassis'"
                   [component]="chassis"
                   [height]="cfg.chassisHeight">
                </g>
                <ng-container *ngIf="chassis.generation === 2 && !cfg.summary">
                    <g identifyLight [component]="chassis" [config]="cfg" [onStatusRect]="true"
                       (toggle)="toggle.emit($event)"
                       [cx]="5" [cy]="cfg.chassisHeight / 2"></g>
                </ng-container>
            </g>
            <g *ngFor="let blade of chassis.blades; let j=index; trackBy: trackByFn"
               [attr.transform]="'translate(' + (cfg.bladeXOffset + j * (cfg.bladeWidth + cfg.bladeGap)) + ', 8)'">
                <rect x="0"
                      y="0"
                      [attr.width]="cfg.bladeWidth"
                      [attr.height]="cfg.bladeHeight"
                      [attr.fill]="blade.status === 'unused' ? 'none' : cfg.defaultFill"
                      [attr.stroke]="blade.status === 'unused' ? cfg.defaultFill : 'none'">
                </rect>
                <g hwStatus
                   [klass]="'blade'"
                   [component]="blade"
                   [x]="cfg.summary ? 2 : 3"
                   [y]="cfg.summary ? 2 : (chassis.generation === 2 ? 4 : 3)"
                   [width]="cfg.bladeWidth - (cfg.summary ? 4 : 6)"
                   [height]="chassis.generation === 2 ? (cfg.summary ? 12 : 14) : cfg.bladeWidth - (cfg.summary ? 4 : 6)"
                   [stroke]="blade.status === 'unused' ? 'none' : '#F0F0F0'"
                   [strokeWidth]="0.5"
                   [summary]="cfg.summary">
                </g>
                <ng-container *ngIf="blade.status !== 'unused' && !cfg.summary">
                    <ng-container *ngIf="chassis.generation === 1">
                        <g identifyLight [component]="blade" [config]="cfg" (toggle)="toggle.emit($event)"
                           [cx]="cfg.bladeWidth / 2"
                           [cy]="cfg.bladeLightYOffset"></g>
                        <line [attr.x1]="cfg.bladeWidth / 2"
                              [attr.y1]="cfg.bladeLightYOffset + 15"
                              [attr.x2]="cfg.bladeWidth / 2"
                              [attr.y2]="cfg.bladeHeight - 10"
                              stroke-width="0.5" stroke="#E8E8E8"></line>
                    </ng-container>
                    <ng-container *ngIf="chassis.generation === 2">
                        <g identifyLight [component]="blade" [config]="cfg" (toggle)="toggle.emit($event)"
                           [cx]="cfg.bladeWidth / 2"
                           [cy]="cfg.bladeLightYOffset"></g>
                        <line x1="4"
                              [attr.y1]="cfg.bladeLightYOffset"
                              [attr.x2]="cfg.bladeWidth / 2 - 7"
                              [attr.y2]="cfg.bladeLightYOffset"
                              stroke="#AAA"></line>
                        <line x1="4"
                              [attr.y1]="cfg.bladeLightYOffset + 0.5"
                              [attr.x2]="cfg.bladeWidth / 2 - 7"
                              [attr.y2]="cfg.bladeLightYOffset + 0.5"
                              stroke="#E8E8E8"></line>
                        <line [attr.x1]="cfg.bladeWidth / 2 + 7"
                              [attr.y1]="cfg.bladeLightYOffset"
                              [attr.x2]="cfg.bladeWidth - 4"
                              [attr.y2]="cfg.bladeLightYOffset"
                              stroke="#AAA"></line>
                        <line [attr.x1]="cfg.bladeWidth / 2 + 7"
                              [attr.y1]="cfg.bladeLightYOffset + 0.5"
                              [attr.x2]="cfg.bladeWidth - 4"
                              [attr.y2]="cfg.bladeLightYOffset + 0.5"
                              stroke="#E8E8E8"></line>
                        <g *ngFor="let drive of blade.drives; let k=index; trackBy: trackByFn"
                           [attr.transform]="'translate(' + (k % 2 === 0 ? 3 : 19) + ',' + (k < 2 ? 23 : 101) + ')'">
                            <rect x="0" y="0" rx="2" stroke-width="0.5"
                                  [attr.width]="cfg.driveWidth"
                                  [attr.height]="cfg.driveHeight"
                                  [attr.stroke]="cfg.driveStroke"
                                  [attr.fill]="drive.status !== 'unused' ? cfg.driveFill : 'none'"></rect>
                            <ng-container *ngIf="drive.status !== 'unused'">
                                <g class="info-tooltip">
                                    <g hwStatus
                                       [klass]="'drive'"
                                       [component]="drive"
                                       [x]="1"
                                       [y]="1"
                                       [width]="cfg.driveWidth-2"
                                       [height]="cfg.driveWidth-2"
                                       [strokeWidth]="0"
                                       [summary]="cfg.summary">
                                    </g>
                                </g>
                                <g identifyLight [component]="drive" [config]="cfg" (toggle)="toggle.emit($event)"
                                   [cx]="6.75" [cy]="51"></g>
                            </ng-container>
                        </g>
                    </ng-container>
                </ng-container>
            </g>
            <ng-container *ngIf="cfg.summary">
                <g *ngFor="let fm of chassis.fabricModules; let j=index; trackBy: trackByFn"
                   [attr.transform]="'translate(' + cfg.bladeXOffset + ', ' + (cfg.bladeHeight + 15 + j * (cfg.fmHeight + 5)) + ')'">
                    <rect x="0" y="0"
                          [attr.width]="cfg.fmWidth"
                          [attr.height]="cfg.fmHeight"
                          [attr.fill]="cfg.fmFill">
                    </rect>
                    <g hwStatus
                       [klass]="'fm'"
                       [component]="fm"
                       [attr.width]="cfg.fmHeight"
                       [attr.height]="cfg.fmHeight">
                    </g>
                </g>
            </ng-container>
        </svg>
    `
                }]
        }], propDecorators: { chassis: [{
                type: Input
            }], toggle: [{
                type: Output
            }] } });

class EfmBackComponent extends GraphicContainerComponent {
    ngOnChanges() {
        if (!this.efm) {
            return;
        }
        this.cfg = this.efm.config;
        if (!this.cfg.summary) {
            setTimeout(() => {
                const ports = [];
                for (const p of this.efm.ethPorts) {
                    ports.push(p);
                }
                if (this.efm.mgmtPort) {
                    ports.push(this.efm.mgmtPort);
                }
                this.applyTooltip({ 'rect.eth.hw-status': ports });
            }, 1);
        }
    }
}
/** @nocollapse */ EfmBackComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: EfmBackComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EfmBackComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.9", type: EfmBackComponent, selector: "fb-hw-efm-back", inputs: { efm: "efm" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: `
        <svg class="fb-gui-hw" [attr.data-instance]="count" [attr.viewBox]="'0 0 ' + (cfg.width) + ' ' + (cfg.efmHeight)">
            <g>
                <rect x="0" y="0" [attr.width]="cfg.width" [attr.height]="cfg.efmHeight"
                      fill="white" [attr.stroke]="cfg.defaultStroke">
                </rect>
            </g>
            <g *ngFor="let port of efm.ethPorts; let i=index; trackBy: trackByFn"
               [attr.transform]="'translate(8, 10)'">
                <g *ngIf="port.portCount !== 2" hwStatus
                   [klass]="'eth'"
                   [component]="port"
                   [x]="(fl(s2i(port.slot) / 2) * 25) + (fl(s2i(port.index) / 2) * 8)"
                   [y]="(s2i(port.slot) % 2) * 15 + (s2i(port.index) % 2) * 5.5"
                   [width]="port.isGrouped ? 15 : 7"
                   [height]="port.isGrouped ? 10 : 4.5">
                </g>
                <g *ngIf="port.portCount === 2" hwStatus
                   [klass]="'eth'"
                   [component]="port"
                   [x]="(fl(s2i(port.slot) / 2) * 25) + (fl(s2i(port.index)) * 8)"
                   [y]="(s2i(port.slot) % 2) * 15"
                   [width]="7"
                   [height]="10">
                </g>
            </g>
            <g *ngIf="efm.mgmtPort"
               [attr.transform]="'translate(422, 25)'">
                <g hwStatus
                   [klass]="'eth'"
                   [component]="efm.mgmtPort">
                </g>
            </g>
            <!-- TODO: eventually need to fix the hard-coded LAG rectangle bug -->
            <g *ngIf="efm.ethPorts.length > 0">
                <rect [attr.x]="3 + 10 * 25"
                      y="5"
                      [attr.width]="4 * 25"
                      height="35"
                      [attr.stroke]="cfg.statusFill"
                      fill="none">
                </rect>
            </g>
        </svg>
    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: StatusComponent, selector: "[hwStatus]", inputs: ["component", "x", "y", "width", "height", "klass", "stroke", "strokeWidth", "summary"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: EfmBackComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fb-hw-efm-back',
                    template: `
        <svg class="fb-gui-hw" [attr.data-instance]="count" [attr.viewBox]="'0 0 ' + (cfg.width) + ' ' + (cfg.efmHeight)">
            <g>
                <rect x="0" y="0" [attr.width]="cfg.width" [attr.height]="cfg.efmHeight"
                      fill="white" [attr.stroke]="cfg.defaultStroke">
                </rect>
            </g>
            <g *ngFor="let port of efm.ethPorts; let i=index; trackBy: trackByFn"
               [attr.transform]="'translate(8, 10)'">
                <g *ngIf="port.portCount !== 2" hwStatus
                   [klass]="'eth'"
                   [component]="port"
                   [x]="(fl(s2i(port.slot) / 2) * 25) + (fl(s2i(port.index) / 2) * 8)"
                   [y]="(s2i(port.slot) % 2) * 15 + (s2i(port.index) % 2) * 5.5"
                   [width]="port.isGrouped ? 15 : 7"
                   [height]="port.isGrouped ? 10 : 4.5">
                </g>
                <g *ngIf="port.portCount === 2" hwStatus
                   [klass]="'eth'"
                   [component]="port"
                   [x]="(fl(s2i(port.slot) / 2) * 25) + (fl(s2i(port.index)) * 8)"
                   [y]="(s2i(port.slot) % 2) * 15"
                   [width]="7"
                   [height]="10">
                </g>
            </g>
            <g *ngIf="efm.mgmtPort"
               [attr.transform]="'translate(422, 25)'">
                <g hwStatus
                   [klass]="'eth'"
                   [component]="efm.mgmtPort">
                </g>
            </g>
            <!-- TODO: eventually need to fix the hard-coded LAG rectangle bug -->
            <g *ngIf="efm.ethPorts.length > 0">
                <rect [attr.x]="3 + 10 * 25"
                      y="5"
                      [attr.width]="4 * 25"
                      height="35"
                      [attr.stroke]="cfg.statusFill"
                      fill="none">
                </rect>
            </g>
        </svg>
    `
                }]
        }], propDecorators: { efm: [{
                type: Input
            }] } });

class EfmFrontComponent extends GraphicContainerComponent {
    constructor() {
        super(...arguments);
        this.toggle = new EventEmitter();
        this.w = 60;
        this.h = 29;
    }
    ngOnChanges() {
        if (!this.efm) {
            return;
        }
        this.cfg = this.efm.config;
        // Note, XFMv1 has 4 fans; XFMv2 has 7 fans.
        // width of a single box: 8 padding on left/right, 10 padding between boxes; assume always have 2 PSUs.
        this.w = ((this.cfg.width - 8 - 8) / (this.efm.fanCount() + 2)) - 10;
        if (!this.cfg.summary) {
            setTimeout(() => {
                this.applyTooltip({
                    'rect.efm.hw-status': [this.efm],
                    'rect.psu.hw-status': this.efm.powerSupplies,
                    'rect.fan.hw-status': this.efm.fans
                });
            }, 1);
        }
    }
}
/** @nocollapse */ EfmFrontComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: EfmFrontComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EfmFrontComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.9", type: EfmFrontComponent, selector: "fb-hw-efm-front", inputs: { efm: "efm" }, outputs: { toggle: "toggle" }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: `
        <svg class="fb-gui-hw" [attr.data-instance]="count" [attr.viewBox]="'0 0 ' + (cfg.width) + ' ' + (cfg.efmHeight)">
            <g>
                <rect x="0" y="0" [attr.width]="cfg.width" [attr.height]="cfg.efmHeight"
                      fill="white" [attr.stroke]="cfg.defaultStroke">
                </rect>
                <g hwStatus
                   [component]="efm"
                   [klass]="'efm'"
                   [height]="cfg.efmHeight"></g>
                <ng-container *ngIf="efm.version >= 2 && !cfg.summary">
                    <!-- XFMv2 supports identify lights -->
                    <g identifyLight [component]="efm" [config]="cfg" [onStatusRect]="true"
                       (toggle)="toggle.emit($event)"
                       [cx]="5" [cy]="cfg.efmHeight / 2"></g>
                </ng-container>
            </g>
            <ng-container *ngIf="cfg.summary">
                <svg:image xlink:href="/images/fb-gui-hw/pure-logo-tuned-down.svg" width="24" height="24" x="142.5" y="12.5"/>
            </ng-container>
            <ng-container *ngIf="!cfg.summary">
                <g *ngFor="let psu of efm.powerSupplies; let i=index; trackBy: trackByFn"
                   [attr.transform]="'translate(' + (18 + i * (cfg.width - w - 18 - 8)) + ', 8)'">
                    <rect x="0" y="0" [attr.width]="w" [attr.height]="h" [attr.fill]="cfg.fmFill"></rect>
                    <g hwStatus
                       [klass]="'psu'"
                       [component]="psu"
                       [x]="5"
                       [y]="5"
                       [strokeWidth]="cfg.statusStrokeWidth"
                       [stroke]="cfg.statusStroke"></g>
                </g>
                <ng-container>
                    <g *ngFor="let fan of efm.fans; index as i; trackBy: trackByFn"
                       [attr.transform]="'translate(' + ((w + 10 + 18) + i * (w + 10)) + ', 8)'">
                        <rect x="0" y="0" [attr.width]="w" [attr.height]="h" [attr.fill]="cfg.fmFill"></rect>
                        <g hwStatus
                           [klass]="'fan'"
                           [component]="fan"
                           [x]="5"
                           [y]="5"
                           [strokeWidth]="cfg.statusStrokeWidth"
                           [stroke]="cfg.statusStroke"></g>
                        <svg:image xlink:href="/images/fb-gui-hw/fan.svg" [attr.x]="w - 15" y="15" width="11" height="11"/>
                    </g>
                </ng-container>
            </ng-container>
        </svg>
    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IdentifyLightComponent, selector: "[identifyLight]", inputs: ["component", "config", "cx", "cy", "onStatusRect"], outputs: ["toggle"] }, { kind: "component", type: StatusComponent, selector: "[hwStatus]", inputs: ["component", "x", "y", "width", "height", "klass", "stroke", "strokeWidth", "summary"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: EfmFrontComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fb-hw-efm-front',
                    template: `
        <svg class="fb-gui-hw" [attr.data-instance]="count" [attr.viewBox]="'0 0 ' + (cfg.width) + ' ' + (cfg.efmHeight)">
            <g>
                <rect x="0" y="0" [attr.width]="cfg.width" [attr.height]="cfg.efmHeight"
                      fill="white" [attr.stroke]="cfg.defaultStroke">
                </rect>
                <g hwStatus
                   [component]="efm"
                   [klass]="'efm'"
                   [height]="cfg.efmHeight"></g>
                <ng-container *ngIf="efm.version >= 2 && !cfg.summary">
                    <!-- XFMv2 supports identify lights -->
                    <g identifyLight [component]="efm" [config]="cfg" [onStatusRect]="true"
                       (toggle)="toggle.emit($event)"
                       [cx]="5" [cy]="cfg.efmHeight / 2"></g>
                </ng-container>
            </g>
            <ng-container *ngIf="cfg.summary">
                <svg:image xlink:href="/images/fb-gui-hw/pure-logo-tuned-down.svg" width="24" height="24" x="142.5" y="12.5"/>
            </ng-container>
            <ng-container *ngIf="!cfg.summary">
                <g *ngFor="let psu of efm.powerSupplies; let i=index; trackBy: trackByFn"
                   [attr.transform]="'translate(' + (18 + i * (cfg.width - w - 18 - 8)) + ', 8)'">
                    <rect x="0" y="0" [attr.width]="w" [attr.height]="h" [attr.fill]="cfg.fmFill"></rect>
                    <g hwStatus
                       [klass]="'psu'"
                       [component]="psu"
                       [x]="5"
                       [y]="5"
                       [strokeWidth]="cfg.statusStrokeWidth"
                       [stroke]="cfg.statusStroke"></g>
                </g>
                <ng-container>
                    <g *ngFor="let fan of efm.fans; index as i; trackBy: trackByFn"
                       [attr.transform]="'translate(' + ((w + 10 + 18) + i * (w + 10)) + ', 8)'">
                        <rect x="0" y="0" [attr.width]="w" [attr.height]="h" [attr.fill]="cfg.fmFill"></rect>
                        <g hwStatus
                           [klass]="'fan'"
                           [component]="fan"
                           [x]="5"
                           [y]="5"
                           [strokeWidth]="cfg.statusStrokeWidth"
                           [stroke]="cfg.statusStroke"></g>
                        <svg:image xlink:href="/images/fb-gui-hw/fan.svg" [attr.x]="w - 15" y="15" width="11" height="11"/>
                    </g>
                </ng-container>
            </ng-container>
        </svg>
    `
                }]
        }], propDecorators: { efm: [{
                type: Input
            }], toggle: [{
                type: Output
            }] } });

class FbGuiHwModule {
}
/** @nocollapse */ FbGuiHwModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: FbGuiHwModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ FbGuiHwModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.9", ngImport: i0, type: FbGuiHwModule, declarations: [ChassisBackComponent,
        ChassisFrontComponent,
        EfmBackComponent,
        EfmFrontComponent,
        GraphicContainerComponent,
        IdentifyLightComponent,
        StatusComponent], imports: [CommonModule], exports: [ChassisBackComponent,
        ChassisFrontComponent,
        EfmBackComponent,
        EfmFrontComponent] });
/** @nocollapse */ FbGuiHwModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: FbGuiHwModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: FbGuiHwModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ChassisBackComponent,
                        ChassisFrontComponent,
                        EfmBackComponent,
                        EfmFrontComponent,
                        GraphicContainerComponent,
                        IdentifyLightComponent,
                        StatusComponent
                    ],
                    exports: [
                        ChassisBackComponent,
                        ChassisFrontComponent,
                        EfmBackComponent,
                        EfmFrontComponent
                    ],
                    imports: [CommonModule]
                }]
        }] });

/*
 * Public API Surface of fb-gui-hw
 */

/**
 * Generated bundle index. Do not edit.
 */

export { Blade, Chassis, ChassisBackComponent, ChassisFrontComponent, Drive, EfmBackComponent, EfmFrontComponent, EthPort, ExternalFabricModule, FabricModule, Fan, FbGuiHwModule, HardwareComponent, IdentifyLightComponent, MgmtPort, PowerSupply, PowerSupplyModule, StorageComponent, VALID_COMPONENT_STATUSES, buildBoxes, componentStatusFromJson, computePortCounts, formatCapacity, formatNetworkSpeed, getColorForStatus, getConfig, getReadableStatus, isNumber, rpcToPureBlade, rpcToPureDrive, rpcToPureHw, sortHw, trackByFn };
